/**
 * Estado/color del next follow-up (una sola regla en toda la app). El vencimiento lo calcula el
 * backend según el sidebar menu donde aterrizó el claim (calendar = llegada + N días; overdue =
 * cadencia del depto). Acá solo se pinta:
 *   aún no vence → ok (verde) · vence hoy o mañana → due (amarillo) · 1 día vencido → late (rojo)
 *   · 2+ días vencidos → manager (alerta al manager; se pinta rojo igual)
 */
export type FollowUpState = 'none' | 'ok' | 'due' | 'late' | 'manager';

const startOfDay = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };

/** Días vencido (0 = vence hoy, negativo = aún no vence). null si no hay follow-up. */
export function daysOverdue(nextIso: string | null | undefined): number | null {
  if (!nextIso) return null;
  const due = new Date(nextIso);
  if (isNaN(due.getTime())) return null;
  return Math.round((startOfDay(new Date()).getTime() - startOfDay(due).getTime()) / 86400000);
}

export function followUpState(nextIso: string | null | undefined): FollowUpState {
  const d = daysOverdue(nextIso);
  if (d == null) return 'none';
  // 'due' = vence HOY o MAÑANA (dueño 2026-10-29: "amarillo si está por vencerse").
  if (d < -1) return 'ok';
  if (d <= 0) return 'due';
  if (d === 1) return 'late';
  return 'manager';
}

export const FOLLOWUP_COLOR: Record<FollowUpState, { border: string; bg: string; fg: string }> = {
  none: { border: 'var(--line)', bg: 'transparent', fg: 'var(--muted)' },
  ok: { border: '#16a34a', bg: '#dcfce7', fg: '#166534' },
  due: { border: '#f59e0b', bg: '#fef3c7', fg: '#92400e' },
  late: { border: '#dc2626', bg: '#fee2e2', fg: '#b91c1c' },
  // 2+ días vencido: ROJO también (dueño 2026-10-29: "rojo si ya está expired"; y 2026-08: "lo late siempre rojo").
  manager: { border: '#dc2626', bg: '#fee2e2', fg: '#b91c1c' },
};

/** Texto corto del follow-up: "in 2d" / "today" / "2d late". */
export function followUpLabel(nextIso: string | null | undefined): string | null {
  const d = daysOverdue(nextIso);
  if (d == null) return null;
  if (d < 0) return `in ${Math.abs(d)}d`;
  if (d === 0) return 'today';
  return `${d}d late`;
}
